/**
 * The intake decision board (#100): every decision of the conversation as tiles, derived from the same state Iris
 * reads, so the board and Iris cannot disagree. Pure: no DOM, no requests.
 */
import type { TalkConcept, TalkMessage } from "./IntakeTalk";

export type BoardPart = "song" | "type" | "story" | "setup" | "cast" | "clothing" | "places" | "treatment";
/** How much of a cinematic clip moves. */
export type MotionSetup = "best" | "good" | "economy";
/** A changed tile keeps the previous value until Iris has taken the change into the script. */
export interface BoardMark { part: BoardPart; key: string; previous: string }
export type BoardClothing =
  | { kind: "look"; lookName: string; thumbnailUrl?: string }
  /** A: a library character; made in the intake after the maker's yes. */
  | { kind: "make"; outfit: string; priceUsd: number | null; assetId: string }
  /** B: a new character; made after the production is created. */
  | { kind: "planned"; outfit: string; priceUsd: number | null }
  | { kind: "none" };
export interface BoardPerson { name: string; role: string; lead: boolean; assetId?: string; portraitUrl?: string; isNew: boolean; clothing: BoardClothing; mark?: BoardMark }
export interface BoardIdea { messageIndex: number; conceptIndex: number; concept: TalkConcept; chosen: boolean }
export interface IntakeBoardInput {
  messages: TalkMessage[];
  answers: { format?: string; shorts?: boolean; story?: string; leadAssetId?: string; leadNewName?: string; supportingCast?: { asset_id: string; role: string }[];
    wardrobe?: { name: string; look?: string; outfit?: string; hair?: string }[];
    companions?: { name: string; kind: "pet" | "prop"; owner: string; existing: boolean }[];
    locations?: { name: string; look: string; story_moment: string }[]; newCast?: { name: string; role: string }[]; motionSetup?: MotionSetup };
  library: { avatars: { id: string; name: string; portraitUrl?: string; looks: { id: string; name: string; approved: boolean; thumbnailUrl?: string }[] }[];
    companions: { id: string; name: string; thumbnailUrl?: string }[] };
  missing: string[];
  /** Null until a treatment is written; then the board changes made since. */
  treatment: null | { changes: number };
  marks: BoardMark[];
  lookPriceUsd: number | null;
  /** The server's price for each setup (cinematic clip only). */
  setupQuotes?: Partial<Record<MotionSetup, { credits: number; withinLimit?: boolean }>>;
}
export interface IntakeBoard {
  path: { part: BoardPart; label: string; state: "done" | "current" | "open" }[];
  /** What is being made: the film type, and whether shorts for socials are the goal. */
  type?: { format: "lyric_video" | "motion_music_video"; shorts: boolean };
  ideas: BoardIdea[]; chosen?: BoardIdea;
  /** The production setup: only for a cinematic clip once a direction is chosen. */
  setup?: { recommended: MotionSetup; why: string; chosen: MotionSetup; credits: Partial<Record<MotionSetup, number>>;
    /** Options whose price is above the server's safety limit. */
    overLimit: Partial<Record<MotionSetup, boolean>> };
  /** The maker's own story, when no direction was chosen: it is the story, the directions stay below. */
  ownStory?: string;
  cast: BoardPerson[];
  companions: { name: string; kind: "pet" | "prop"; owner: string; isNew: boolean; thumbnailUrl?: string; mark?: BoardMark }[];
  places: { name: string; look: string; mark?: BoardMark }[];
  treatment: null | { upToDate: boolean; changes: number };
  marks: BoardMark[];
}

const LABELS: Record<BoardPart, string> = { song: "Song", type: "Type", story: "Story", setup: "Setup", cast: "Cast", clothing: "Clothing", places: "Places", treatment: "Treatment" };
const SONG_KEYS = ["audio", "title", "artist", "lyrics"];
const same = (a: string | undefined, b: string | undefined) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

function latestIdeas(messages: TalkMessage[]): BoardIdea[] {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]!;
    if (message.concepts?.length)
      return message.concepts.map((concept, conceptIndex) => ({ messageIndex: index, conceptIndex, concept, chosen: message.chosen === conceptIndex }));
  }
  return [];
}

/** The chosen direction, from whichever set it came: asking for three other directions never hides it. */
function chosenIdea(messages: TalkMessage[]): BoardIdea | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]!, concept = Number.isInteger(message.chosen) ? message.concepts?.[message.chosen!] : undefined;
    if (concept) return { messageIndex: index, conceptIndex: message.chosen!, concept, chosen: true };
  }
  return undefined;
}

function clothingFor(input: IntakeBoardInput, name: string, avatar: IntakeBoardInput["library"]["avatars"][number] | undefined): BoardClothing {
  const worn = (input.answers.wardrobe || []).find((item) => same(item.name, name));
  if (worn?.look) {
    const look = avatar?.looks.find((item) => same(item.name, worn.look));
    if (look) return { kind: "look", lookName: look.name, ...(look.thumbnailUrl ? { thumbnailUrl: look.thumbnailUrl } : {}) };
  }
  const outfit = String(worn?.outfit || "").trim();
  if (!outfit) return { kind: "none" };
  return avatar ? { kind: "make", outfit, priceUsd: input.lookPriceUsd, assetId: avatar.id } : { kind: "planned", outfit, priceUsd: input.lookPriceUsd };
}

export function intakeBoard(input: IntakeBoardInput): IntakeBoard {
  const { answers, library, marks } = input;
  const markFor = (part: BoardPart, key: string) => marks.find((mark) => mark.part === part && same(mark.key, key));
  const ideas = latestIdeas(input.messages);
  const chosen = chosenIdea(input.messages);
  const format = answers.format === "motion_music_video" ? "motion_music_video" : answers.format === "lyric_video" || answers.shorts ? "lyric_video" : "";
  const type = format ? { format, shorts: !!answers.shorts } as const : undefined;
  const ownStory = !chosen && String(answers.story || "").trim() ? String(answers.story).trim() : undefined;
  const recommended: MotionSetup = chosen?.concept.setup ?? "good";
  const setup = type?.format === "motion_music_video" && chosen
    ? { recommended, why: chosen.concept.setup_why ?? "", chosen: answers.motionSetup ?? recommended,
        credits: Object.fromEntries(Object.entries(input.setupQuotes ?? {}).map(([key, quote]) => [key, quote!.credits])) as Partial<Record<MotionSetup, number>>,
        overLimit: Object.fromEntries(Object.entries(input.setupQuotes ?? {}).filter(([, quote]) => quote!.withinLimit === false).map(([key]) => [key, true])) as Partial<Record<MotionSetup, boolean>> }
    : undefined;

  const cast: BoardPerson[] = [];
  const addPerson = (name: string, role: string, lead: boolean) => {
    if (!name || cast.some((person) => same(person.name, name))) return;
    const avatar = library.avatars.find((item) => same(item.name, name));
    cast.push({
      name: avatar?.name || name, role, lead, isNew: !avatar, clothing: clothingFor(input, name, avatar),
      ...(avatar ? { assetId: avatar.id } : {}), ...(avatar?.portraitUrl ? { portraitUrl: avatar.portraitUrl } : {}),
      ...((markFor("cast", name) || markFor("clothing", name)) ? { mark: markFor("cast", name) || markFor("clothing", name) } : {}),
    });
  };
  const leadAvatar = library.avatars.find((item) => item.id === answers.leadAssetId);
  if (leadAvatar) addPerson(leadAvatar.name, "lead", true);
  else if (answers.leadNewName) addPerson(answers.leadNewName, "lead", true);
  for (const item of answers.supportingCast || []) addPerson(library.avatars.find((avatar) => avatar.id === item.asset_id)?.name || "", item.role, false);
  for (const person of answers.newCast || []) addPerson(person.name, person.role, false);

  const companions = (answers.companions || []).map((item) => {
    const known = library.companions.find((companion) => same(companion.name, item.name));
    const mark = markFor("cast", item.name);
    return { name: item.name, kind: item.kind, owner: item.owner, isNew: !item.existing,
      ...(known?.thumbnailUrl ? { thumbnailUrl: known.thumbnailUrl } : {}), ...(mark ? { mark } : {}) };
  });
  const places = (answers.locations || []).map((place) => {
    const mark = markFor("places", place.name);
    return { name: place.name, look: place.look, ...(mark ? { mark } : {}) };
  });
  const treatment = input.treatment ? { upToDate: input.treatment.changes === 0, changes: input.treatment.changes } : null;

  const missing = new Set(input.missing);
  // Iris only asks about clothing once there is a cast, and about places once there is a story: until then they are open.
  const story = !!chosen || !missing.has("story"), castDone = !missing.has("cast") && cast.length > 0;
  const done: Record<BoardPart, boolean> = {
    song: SONG_KEYS.every((key) => !missing.has(key)),
    type: !!type,
    story,
    setup: !!setup,
    cast: castDone,
    clothing: castDone && !missing.has("wardrobe"),
    places: story && !missing.has("places"),
    treatment: !!treatment?.upToDate,
  };
  let current = false;
  const path = (Object.keys(LABELS) as BoardPart[]).filter((part) => part !== "setup" || type?.format === "motion_music_video").map((part) => {
    const state: "done" | "current" | "open" = done[part] ? "done" : current ? "open" : ((current = true), "current");
    return { part, label: LABELS[part], state };
  });

  return { path, ...(type ? { type } : {}), ideas, ...(chosen ? { chosen } : {}), ...(setup ? { setup } : {}), ...(ownStory ? { ownStory } : {}), cast, companions, places, treatment, marks };
}
